import { fetchCartSummary, fetchCart, removeProduct, updateProductQty } from 'helpers/api';
import { formatMoney, hasClass } from 'helpers/utils';
import { updateAllowableFreight } from './allowable-freight-promo';
import { closeMobileAccount } from './dealer-portal-navigation';
import { store } from 'js/modules/vue/store';

const _sidecart = document.getElementsByClassName('sidecart')[0];
const _cartOverlay = document.getElementsByClassName('sidecart__overlay')[0];
const _scToggles = Array.from(document.querySelectorAll('html, body, #content'));
const _cartItems = Array.from(document.getElementsByClassName('sidecart__items'));
const _checkout = document.getElementsByClassName('sidecart__checkout')[0];

const state = {
    cartFetched: false,
};

// The annual-return page drives its own allowable-freight bar off the
// 'return' cart (see annual-return.vue's refreshAllowableFreight). Its
// markup shares the same js--ob-percent/js--allowable-freight classes as
// this module's regular-cart freight bar, so this global bootstrap must
// not overwrite it with regular-cart data.
function isAnnualReturnPage() {
    return document.getElementsByClassName('annual-return').length > 0;
}

// ==================================================
// INITIALIZERS
// ==================================================

export async function getCartCount(cartType = 'regular') {
    const cartSummary = await fetchCartSummary(cartType);

    updateCartTotal({
        total: cartSummary.CartTotal,
        count: cartSummary.QuantityCount,
    });

    if (!isAnnualReturnPage()) {
        updateAllowableFreight({
            summary: cartSummary,
        });
    }

    store.dispatch('UpdateItemsCount');

    return cartSummary;
}

export async function getCart(cartObj = null) {
    const scList = document.getElementsByClassName('js--sc-items')[0];
    const res = cartObj || (await fetchCart());

    if (!state.cartFetched) {
        const emptyTitle = document.getElementsByClassName('js--sc-empty')[0];

        emptyTitle.innerHTML = emptyTitle.getAttribute('data-copy');
    }

    if (!res.CartItems.length) {
        return;
    }

    updateCartListView();
    state.cartFetched = true;

    scList.innerHTML = res.CartItems.reverse()
        .map((k) =>
            cardItem({
                qty: k.Quantity,
                title: k.NetSuiteItem.Manufacturer + ', ' + k.NetSuiteItem.Legacy_Part_Number,
                desc: k.NetSuiteItem.SalesDescription,
                price: k.ActualDealerCost,
                //availability: k.Availability,
                id: k.Id,
                BackorderQuantity: k.BackorderQuantity || 0,
                SmartshipQuantity: k.SmartShipQuantity || 0,
                ETADate: k.ETADate,
                AlternateWarehouseNote: k.AlternateWarehouseNote,
            }),
        )
        .join('');

    Array.from(document.getElementsByClassName('js--sc-remove')).forEach((k) =>
        k.addEventListener('click', (e) => {
            e.preventDefault();
            remove(k);
        }),
    );
}

// ==================================================
// HANDLERS
// ==================================================

function updateCartListView() {
    const _list = document.getElementsByClassName('js--sc-list')[0];
    const _emptyText = document.getElementsByClassName('js--sc-empty')[0];
    const _checkout = document.getElementsByClassName('js--sc-checkout')[0];

    const listEmpty = 'sidecart__items--empty';
    const hideEmptyText = 'sidecart__empty-text--hide';
    const checkoutDisabled = 'sidecart__checkout--hide';

    _list.classList.remove(listEmpty);
    _emptyText.classList.add(hideEmptyText);
    _checkout.classList.remove(checkoutDisabled);
}

async function qtyChange(item) {
    const cartItem = item.closest('.js--sc-item');
    const productId = cartItem.getAttribute('data-id');
    const opr = item.classList.contains('js--sc-plus');
    const cartItemInput = cartItem.getElementsByClassName('js--sc-amount')[0];

    let qty =
        item === cartItemInput
            ? parseInt(cartItemInput.value, 10)
            : parseInt(cartItemInput.value, 10) + (opr ? 1 : -1);

    if (item === cartItemInput && (qty === '' || qty === '0' || typeof qty !== 'number')) {
        qty = 1;
        cartItemInput.value = 1;
    }

    const { Quantity, Product } = await updateProductQty({
        qty,
        cartItemId: productId,
    });

    const cartSummary = await fetchCartSummary();

    updateCartItem({
        cartItem,
        cartSummary,
        price: Product.ActualCost,
        qty: Quantity,
        availability: Product.Availability,
        BackorderQuantity: Product.BackorderQuantity,
        SmartshipQuantity: Product.SmartshipQuantity,
    });

    if (!isAnnualReturnPage()) {
        updateAllowableFreight({
            summary: cartSummary,
        });
    }
}

function updateCartItem({
    cartItem,
    price,
    qty,
    availability,
    cartSummary,
    BackorderQuantity,
    SmartshipQuantity,
}) {
    const cartItemPrice = cartItem.getElementsByClassName('js--sc-price')[0];
    const cartItemTotal = cartItem.getElementsByClassName('js--sc-total')[0];
    const cartItemInput = cartItem.getElementsByClassName('js--sc-amount')[0];
    const cartItemAvailability = cartItem.getElementsByClassName('js--sc-availability')[0];
    const cartTotal = Array.from(document.getElementsByClassName('js--cart-total'));
    const cartQuantity = Array.from(document.getElementsByClassName('js--cart-count'));
    const cartBackOrder = cartItem.getElementsByClassName('bo-sm')[0];
    const cartSmartShip = cartItem.getElementsByClassName('ss-sm')[0];
    const cartQty = cartItem.getElementsByClassName('qty-sm')[0];
    const qtyDisplay =
        cartSummary.QuantityCount > 1000
            ? `${Math.floor(cartSummary.QuantityCount / 1000)}K+`
            : cartSummary.QuantityCount;

    if (qty < 1) {
        remove(cartItem);
    }

    cartItemPrice.innerHTML = formatMoney(price);
    cartItemInput.value = qty;
    cartItemTotal.innerHTML = formatMoney(parseFloat(price * qty));
    cartTotal.forEach((k) => (k.innerHTML = formatMoney(cartSummary.CartTotal)));
    cartQuantity.forEach((k) => (k.innerHTML = qtyDisplay));

    if (cartItemAvailability) {
        if (parseInt(cartItemAvailability.getAttribute('data-status'), 10) !== availability) {
            cartItemAvailability.innerHTML = itemStatus(availability);
        }
    }

    if (BackorderQuantity > 0) {
        if (cartBackOrder.classList.contains('hide-method')) {
            cartBackOrder.classList.remove('hide-method');
        }
        cartBackOrder.getElementsByClassName(
            'sm-bo',
        )[0].innerHTML = `${BackorderQuantity} Items Backordered`;
    } else {
        if (!cartBackOrder.classList.contains('hide-method')) {
            cartBackOrder.classList.add('hide-method');
        }
    }

    if (SmartshipQuantity > 0) {
        if (cartSmartShip.classList.contains('hide-method')) {
            cartSmartShip.classList.remove('hide-method');
        }
        cartSmartShip.getElementsByClassName(
            'sm-ss',
        )[0].innerHTML = `${SmartshipQuantity} Items Available Via Enhanced Shipping`;
    } else {
        if (!cartSmartShip.classList.contains('hide-method')) {
            cartSmartShip.classList.add('hide-method');
        }
    }

    if (qty - SmartshipQuantity - BackorderQuantity > 0) {
        if (cartQty.classList.contains('hide-method')) {
            cartQty.classList.remove('hide-method');
        }
        cartQty.getElementsByClassName('sm-qty')[0].innerHTML = `${qty - SmartshipQuantity - BackorderQuantity
            } Items available`;
    } else {
        if (!cartQty.classList.contains('hide-method')) {
            cartQty.classList.add('hide-method');
        }
    }
}

export function updateCartTotal({ count, total }) {
    const _cartTotals = Array.from(document.getElementsByClassName('js--cart-total'));
    const _cartCounts = Array.from(document.getElementsByClassName('js--cart-count'));

    _cartTotals.forEach((k) => (k.innerHTML = formatMoney(total)));
    _cartCounts.forEach((k) => (k.innerHTML = count));
}

function closeCart() {
    _sidecart.classList.add('sidecart--close');
    _cartOverlay.classList.add('sidecart__overlay--hide');

    setTimeout(() => _scToggles.forEach((k) => k.classList.remove('sidecart--open')), 400);
}

export function openCart() {
    if (document.getElementsByClassName('checkout').length) {
        return;
    }

    _sidecart.classList.remove('sidecart--close');
    _cartOverlay.classList.remove('sidecart__overlay--hide');
    _scToggles.forEach((k) => k.classList.add('sidecart--open'));
}

async function remove(item) {
    removeAnimation(item);
    const status = await removeProduct(item.getAttribute('data-id'), 'sidecart:remove');

    if (status === 200) {
        getCartCount();
    }
}

function removeAnimation(item) {
    const _this = item;

    _this.closest('.sidecart__item').classList.add('sidecart__item--remove');

    setTimeout(() => {
        const length =
            document
                .getElementsByClassName('sidecart__items')[0]
                .getElementsByClassName('sidecart__item').length - 1;
        _this.closest('.sidecart__item').parentNode.removeChild(_this.closest('.sidecart__item'));

        if (length < 1) {
            _cartItems.forEach((k) => k.classList.add('sidecart__items--empty'));
            document
                .getElementsByClassName('sidecart__empty-text')[0]
                .classList.remove('sidecart__empty-text--hide');
            _checkout.classList.add('sidecart__checkout--hide');
        }
    }, 500);
}

// ==================================================
// HELPERS
// ==================================================

const cardItem = ({
    qty,
    title,
    desc,
    price,
    //availability,
    id,
    BackorderQuantity,
    SmartshipQuantity,
    ETADate,
    AlternateWarehouseNote,
}) => `
  <div class="sidecart__item js--sc-item" data-id="${id}">
  <a href="/" data-id="${id}" class="sidecart__item--delete js--sc-remove">
    <i class="fas fa-times" aria-hidden="true" title="Remove Item"></i>
  </a>
  <p class="sidecart__item__title">${title}</p>
  <p class="sidecart__item__description">${desc}</p>
  ${AlternateWarehouseNote
        ? `<p class="sidecart__item__description"><i class="fa fa-truck"></i> ${AlternateWarehouseNote}</p>`
        : ''
    }
  <div class="sidecart__item__totals">
    <p class="sidecart__item__price"><span class="js--sc-price">${formatMoney(price)}</span>
      <span class="sidecart__item__price-accent">Ea.</span>
    </p>
    <div class="sidecart__amount js--sc-qty-options">
      <input type="text" class="sidecart__amount-input txtbox js--sc-amount" value="${qty}"
        min="0">
    </div>
    <p class="sidecart__item__total js--sc-total" data-price="${price}">${formatMoney(
        qty * price,
    )}</p>
  </div>
  <span>
    <div class="ship-method qty-sm${qty - SmartshipQuantity - BackorderQuantity > 0 ? '' : ' hide-method'
    }"><i aria-hidden="true" class="ob__overview__icon success fa fa-check"></i> <span class="sm-qty">${qty - SmartshipQuantity - BackorderQuantity
    } Items available</span></div>
    <div class="ship-method ss-sm${SmartshipQuantity > 0 ? '' : ' hide-method'
    }"><img src="/assets/smartship.png" class="ob__overview__icon"> <span class="sm-ss">${SmartshipQuantity} Items Available Via Enhanced Shipping</span></div>
    <div class="ship-method bo-sm${BackorderQuantity > 0 ? '' : ' hide-method'
    }"><i aria-hidden="true" class="ob__overview__icon alert fa fa-exclamation-triangle"></i> <span class="sm-bo">${BackorderQuantity} Items Backordered</span></div>
    <div class="ship-method eta${ETADate ? '' : ' hide-method'
    }"><span class="ob__results--bold">ETA:</span><span>\u00A0${ETADate}</span></div>
  </span>
</div>`;

// const itemStatus = (availability) =>
//   `<p class="sidecart__alert">
//   ${availability === 300 ? '<i class="alert fas fa-exclamation-triangle"></i>' : availability === 100 ? '<i class="fa fa-check success"></i>' : '<img class="smartship-icon" src="/images/smartship.png">'} ${availability === 300
//     ? 'Backordered'
//     : availability === 100 ? 'Available' : 'SmartShip'
//   }</p>`

// ==================================================
// INIT
// ==================================================

function init() {
    const _cartIcon = document.getElementsByClassName('js--cartIcon')[0];
    const _closeEls = Array.from(document.querySelectorAll('.sidecart__close, .sidecart__overlay'));

    _closeEls.forEach((k) =>
        k.addEventListener('click', (e) => {
            e.preventDefault();
            closeCart();
        }),
    );

    _cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        closeMobileAccount();
        openCart();
    });

    Array.from(document.getElementsByClassName('sidecart__item--delete')).forEach((k) =>
        k.addEventListener('click', (e) => {
            e.preventDefault();
            remove(e.target);
        }),
    );

    Array.from(['click', 'change']).forEach((k) =>
        document.addEventListener(
            k,
            (e) =>
                hasClass(e.target, ['js--sc-plus', 'js--sc-minus', 'js--sc-amount'])
                    ? qtyChange(e.target)
                    : false,
            false,
        ),
    );

    Array.from(document.getElementsByClassName('sidecart--open')).forEach((k) =>
        k.addEventListener('touchmove', (e) => e.preventDefault()),
    );

    if (!document.getElementsByClassName('js--checkout-page').length) {
        getCartCount();

        _cartIcon.addEventListener('click', () => (state.cartFetched ? false : getCart()));
    }
}

export default init;
